function toFiniteFactNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function getPrimaryPlaceCategory(types = []) {
  return (
    (Array.isArray(types) ? types : [])
      .map((type) => String(type || "").replace(/_/g, " ").trim())
      .filter(
        (type) =>
          type &&
          !["point of interest", "establishment"].includes(type.toLowerCase())
      )[0] || ""
  );
}

function normalizePhoneHref(phone = "") {
  const text = String(phone || "").trim();
  if (!text) return "";
  const hasLeadingPlus = text.startsWith("+");
  const digits = text.replace(/\D/g, "");
  if (digits.length < 7) return "";
  return `tel:${hasLeadingPlus ? "+" : ""}${digits}`;
}

function toOffsetMinutes(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function parseOpeningTime(time = "") {
  const text = String(time || "").trim();
  if (!/^\d{4}$/.test(text)) return null;
  const hours = Number(text.slice(0, 2));
  const minutes = Number(text.slice(2));
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function normalizeOpeningPoint(point = {}) {
  const day = Number(point?.day);
  const minutes = parseOpeningTime(point?.time);
  if (!Number.isInteger(day) || day < 0 || day > 6 || minutes == null) return null;
  return { day, minutes };
}

function localClockFromOffset(now, utcOffsetMinutes) {
  const timestamp = now instanceof Date ? now.getTime() : Number.NaN;
  if (!Number.isFinite(timestamp)) return null;
  const shifted = new Date(timestamp + utcOffsetMinutes * 60 * 1000);
  return {
    day: shifted.getUTCDay(),
    minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
  };
}

function minutesFromWeekStart(point) {
  return point.day * 24 * 60 + point.minutes;
}

function formatTime(minutes) {
  const hours24 = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(mins).padStart(2, "0")} ${suffix}`;
}

function formatOpeningDetail(point, currentDay) {
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const time = formatTime(point.minutes);
  return point.day === currentDay
    ? `Opens at ${time}`
    : `Opens ${dayNames[point.day]} at ${time}`;
}

function buildOpenStatus(placeFacts = {}, options = {}) {
  const facts = placeFacts && typeof placeFacts === "object" ? placeFacts : {};
  const periods = Array.isArray(facts.openingPeriods) ? facts.openingPeriods : [];
  const utcOffsetMinutes = toOffsetMinutes(facts.utcOffsetMinutes ?? facts.utcOffset);
  if (!periods.length || utcOffsetMinutes == null) return null;

  const now = options.now instanceof Date ? options.now : new Date();
  const localNow = localClockFromOffset(now, utcOffsetMinutes);
  if (!localNow) return null;

  const nowWeekMinute = minutesFromWeekStart(localNow);
  const normalizedPeriods = periods
    .map((period) => ({
      open: normalizeOpeningPoint(period?.open),
      close: period?.close ? normalizeOpeningPoint(period.close) : null,
    }))
    .filter((period) => period.open);

  for (const period of normalizedPeriods) {
    const openMinute = minutesFromWeekStart(period.open);
    const closeMinute = period.close
      ? minutesFromWeekStart(period.close) + (period.close.day < period.open.day ? 7 * 24 * 60 : 0)
      : openMinute + 7 * 24 * 60;
    const candidateNow = nowWeekMinute < openMinute ? nowWeekMinute + 7 * 24 * 60 : nowWeekMinute;
    if (candidateNow >= openMinute && candidateNow < closeMinute) {
      const closeLocalMinutes = period.close ? period.close.minutes : 0;
      const detail = period.close ? `Closes at ${formatTime(closeLocalMinutes)}` : "Open 24 hours";
      return {
        tone: "open",
        label: "Open now",
        detail,
        text: `Open now · ${detail}`,
      };
    }
  }

  const upcoming = normalizedPeriods
    .map((period) => {
      const openMinute = minutesFromWeekStart(period.open);
      return {
        ...period.open,
        delta: openMinute >= nowWeekMinute
          ? openMinute - nowWeekMinute
          : openMinute + 7 * 24 * 60 - nowWeekMinute,
      };
    })
    .sort((a, b) => a.delta - b.delta)[0];

  if (!upcoming) return null;
  const detail = formatOpeningDetail(upcoming, localNow.day);
  return {
    tone: "closed",
    label: "Closed now",
    detail,
    text: `Closed now · ${detail}`,
  };
}

export function buildPlaceVisitInfo(placeFacts = {}, options = {}) {
  const facts = placeFacts && typeof placeFacts === "object" ? placeFacts : {};
  const rating = toFiniteFactNumber(facts.rating);
  const reviewCount = toFiniteFactNumber(facts.reviewCount);
  const priceLevel = toFiniteFactNumber(facts.priceLevel);
  const category = getPrimaryPlaceCategory(facts.types);
  const website = String(facts.website || "").trim();
  const phoneHref = normalizePhoneHref(
    facts.internationalPhoneNumber || facts.formattedPhoneNumber
  );
  const visitFacts = [];
  const actions = [];

  if (rating && rating > 0) {
    visitFacts.push({
      label: "Rating",
      value: `${rating.toFixed(1)}${
        reviewCount && reviewCount > 0
          ? ` (${Math.round(reviewCount).toLocaleString()} reviews)`
          : ""
      }`,
    });
  }
  if (priceLevel && priceLevel > 0) {
    visitFacts.push({
      label: "Price",
      value: "$".repeat(Math.min(priceLevel, 4)),
    });
  }
  if (category) {
    visitFacts.push({ label: "", value: category });
  }

  if (website) {
    actions.push({ label: "Website", href: website, type: "website" });
  }
  if (phoneHref) {
    actions.push({ label: "Call", href: phoneHref, type: "call" });
  }

  return {
    facts: visitFacts,
    actions,
    openStatus: buildOpenStatus(facts, options),
    hours:
      Array.isArray(facts.weekdayText) && facts.weekdayText.length
        ? {
            label: "Weekly hours",
            value: facts.weekdayText.slice(0, 7).join("\n"),
          }
        : null,
  };
}
